// =====================================================
// TEMPLATES ADMIN PAGE - v2 con cálculo de precios
// =====================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Link2,
  Download,
  Save,
  Loader2,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Trash2,
  GripVertical,
  AlertTriangle,
  CheckCircle2,
  Eye,
  DollarSign,
  Building2,
  Tag,
  Info,
  ChevronDown,
  ChevronUp,
  LinkIcon,
  Calculator,
  Stethoscope,
  X,
} from 'lucide-react';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal, type ImportResult } from '../../components/ui/ImportExportModal';
import {
  templatesApi,
  type Template,
  type TemplateLinkedSite,
  type PaginatedTemplates,
  type TemplateItemInput,
  type TariffInfo,
  type SocietyInfo,
  type MissingActInfo,
  type TemplatePreview,
} from '../../api/templates';
import { medicalActsApi } from '../../api/medical-acts';
import type { MedicalActSelect } from '../../api/medical-acts';

// ----------------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------------

interface TemplateFormData {
  code: string;
  name: string;
  description: string;
}

type SortField = 'name' | 'code' | 'createdAt';

interface EditableTemplateItem extends TemplateItemInput {
  id?: string;
  medicalActCode?: string;
  medicalActName?: string;
  treatmentGroupName?: string;
  priceInfo?: {
    finalPrice: number;
    source: string;
    sourceLabel: string;
  };
  lineSubtotal?: number;
}

// ----------------------------------------------------------------------------
// HELPERS
// ----------------------------------------------------------------------------
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

const PriceSourceBadge = ({ source, sourceLabel }: { source: string; sourceLabel: string }) => {
  const colors: Record<string, string> = {
    act: 'bg-blue-100 text-blue-800',
    site: 'bg-green-100 text-green-800',
    tariff: 'bg-purple-100 text-purple-800',
    override: 'bg-orange-100 text-orange-800',
    none: 'bg-gray-100 text-gray-500',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[source] || colors.none}`}>
      {sourceLabel}
    </span>
  );
};

// ----------------------------------------------------------------------------
// COMPONENT
// ----------------------------------------------------------------------------

export function TemplatesAdminPage() {
  // Data states
  const [data, setData] = useState<PaginatedTemplates | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Template | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Items modal
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [templateItems, setTemplateItems] = useState<EditableTemplateItem[]>([]);
  const [availableActs, setAvailableActs] = useState<MedicalActSelect[]>([]);
  const [availableTariffs, setAvailableTariffs] = useState<TariffInfo[]>([]);
  const [availableSocieties, setAvailableSocieties] = useState<SocietyInfo[]>([]);
  const [isLoadingActs, setIsLoadingActs] = useState(false);
  const [selectedActId, setSelectedActId] = useState<string>('');
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);

  // Link modal
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkingTemplate, setLinkingTemplate] = useState<Template | null>(null);
  const [linkedSites, setLinkedSites] = useState<TemplateLinkedSite[]>([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);
  
  // Quick link modal
  const [isQuickLinkModalOpen, setIsQuickLinkModalOpen] = useState(false);
  const [quickLinkSite, setQuickLinkSite] = useState<TemplateLinkedSite | null>(null);
  const [quickLinkAct, setQuickLinkAct] = useState<MissingActInfo | null>(null);
  const [quickLinkPrice, setQuickLinkPrice] = useState<number>(0);
  const [quickLinkSocieties, setQuickLinkSocieties] = useState<string[]>([]);
  const [isSavingQuickLink, setIsSavingQuickLink] = useState(false);

  // State para verificación de centros afectados al añadir acto
  const [itemsLinkedSites, setItemsLinkedSites] = useState<TemplateLinkedSite[]>([]);
  const [affectedSitesDialog, setAffectedSitesDialog] = useState<{
    isOpen: boolean;
    actId: string;
    actCode: string;
    actName: string;
    affectedSites: Array<{ siteId: string; siteName: string; siteCode: string }>;
  } | null>(null);
  const [pendingAddItem, setPendingAddItem] = useState<{
    act: MedicalActSelect;
    quantity: number;
  } | null>(null);

  // Preview modal
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [previewData, setPreviewData] = useState<TemplatePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewSiteId, setPreviewSiteId] = useState<string>('');
  const [previewSites, setPreviewSites] = useState<Array<{ id: string; code: string; name: string }>>([]);

  // Confirm dialog - UNIFICADO igual que Specialties
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: Template | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Import/Export
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TemplateFormData>();

  // --------------------------------------------------------------------------
  // DATA LOADING
  // --------------------------------------------------------------------------
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await templatesApi.getAll({
        page,
        limit,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
        includeInactive: showInactive,
        includeItems: true,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar plantillas');
      toast.error('Error al cargar plantillas');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --------------------------------------------------------------------------
  // LOAD AUXILIARY DATA
  // --------------------------------------------------------------------------
  const loadAuxiliaryData = useCallback(async () => {
    setIsLoadingActs(true);
    try {
      const [acts, tariffs, societies] = await Promise.all([
        medicalActsApi.getForSelect(),
        templatesApi.getAvailableTariffs(),
        templatesApi.getAvailableSocieties(),
      ]);
      setAvailableActs(acts);
      setAvailableTariffs(tariffs);
      setAvailableSocieties(societies);
    } catch (err) {
      toast.error('Error al cargar datos auxiliares');
    } finally {
      setIsLoadingActs(false);
    }
  }, []);

  // --------------------------------------------------------------------------
  // HANDLERS: MODAL CREAR/EDITAR
  // --------------------------------------------------------------------------
  const handleOpenModal = (item?: Template) => {
    if (item) {
      setEditingItem(item);
      reset({
        code: item.code,
        name: item.name,
        description: item.description || '',
      });
    } else {
      setEditingItem(null);
      reset({ code: '', name: '', description: '' });
    }
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormError(null);
    reset();
  };

  const handleSave = async (formData: TemplateFormData) => {
    setIsSaving(true);
    setFormError(null);
    try {
      if (editingItem) {
        await templatesApi.update(editingItem.id, formData);
        toast.success('Plantilla actualizada');
      } else {
        await templatesApi.create(formData);
        toast.success('Plantilla creada');
      }
      handleCloseModal();
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // HANDLERS: MODAL LÍNEAS
  // --------------------------------------------------------------------------
  const handleOpenItemsModal = async (template: Template) => {
    setEditingTemplate(template);
    setIsItemsModalOpen(true);
    
    await loadAuxiliaryData();
    
    // Cargar linkedSites para verificar impacto al añadir actos
    try {
      const sites = await templatesApi.getLinkedSites(template.id);
      setItemsLinkedSites(sites);
    } catch (err) {
      console.error('Error al cargar centros vinculados:', err);
      setItemsLinkedSites([]);
    }
    
    try {
      const fullTemplate = await templatesApi.getById(template.id);
      if (fullTemplate.items) {
        setTemplateItems(
          fullTemplate.items.map((item) => ({
            medicalActId: item.medicalActId,
            medicalActCode: item.medicalActCode,
            medicalActName: item.medicalActName,
            treatmentGroupName: item.treatmentGroupName,
            tariffId: item.tariffId,
            societyId: item.societyId,
            quantity: item.quantity,
            priceOverride: item.priceOverride,
            notes: item.notes || '',
            sortOrder: item.sortOrder,
            id: item.id,
            priceInfo: {
              finalPrice: item.priceInfo.finalPrice,
              source: item.priceInfo.source,
              sourceLabel: item.priceInfo.sourceLabel,
            },
            lineSubtotal: item.lineSubtotal,
          }))
        );
      } else {
        setTemplateItems([]);
      }
    } catch (err) {
      toast.error('Error al cargar líneas de la plantilla');
      setTemplateItems([]);
    }
  };

  const handleCloseItemsModal = () => {
    setIsItemsModalOpen(false);
    setEditingTemplate(null);
    setTemplateItems([]);
    setSelectedActId('');
    setSelectedQuantity(1);
    setExpandedItemId(null);
    setItemsLinkedSites([]);
    setAffectedSitesDialog(null);
    setPendingAddItem(null);
  };

  // NUEVO: Añadir item con verificación de centros afectados
  const handleAddItem = async () => {
    if (!selectedActId) return;

    if (templateItems.some((i) => i.medicalActId === selectedActId)) {
      toast.warning('Este acto ya está en la plantilla');
      return;
    }

    const act = availableActs.find((a) => a.id === selectedActId);
    if (!act) return;

    // Verificar si hay centros vinculados que se verían afectados
    const linkedSitesWithTemplate = itemsLinkedSites.filter(s => s.isLinked);
    
    if (linkedSitesWithTemplate.length > 0) {
      try {
        // Obtener los centros donde el acto está vinculado
        const actLinkedSites = await medicalActsApi.getLinkedSites(selectedActId);
        const actLinkedSiteIds = new Set(
          actLinkedSites.filter(s => s.isLinked).map(s => s.siteId)
        );

        // Encontrar centros vinculados a la plantilla donde el acto NO está vinculado
        const affectedSites = linkedSitesWithTemplate.filter(
          site => !actLinkedSiteIds.has(site.siteId)
        );

        if (affectedSites.length > 0) {
          // Mostrar diálogo de confirmación
          setAffectedSitesDialog({
            isOpen: true,
            actId: act.id,
            actCode: act.code,
            actName: act.name,
            affectedSites: affectedSites.map(s => ({
              siteId: s.siteId,
              siteName: s.siteName,
              siteCode: s.siteCode,
            })),
          });
          setPendingAddItem({ act, quantity: selectedQuantity });
          return; // Esperar confirmación del usuario
        }
      } catch (err) {
        console.error('Error al verificar centros:', err);
        // Si hay error, continuar sin verificación (mejor UX)
      }
    }

    // Si no hay centros afectados, añadir directamente
    await doAddItem(act, selectedQuantity);
  };

  // Función auxiliar para añadir el item
  const doAddItem = async (act: MedicalActSelect, quantity: number) => {
    setIsAddingItem(true);
    try {
      // Llamar al endpoint para calcular el precio correcto
      const priceResult = await templatesApi.calculatePrice(act.id);

      const newItem: EditableTemplateItem = {
        medicalActId: act.id,
        medicalActCode: act.code,
        medicalActName: act.name,
        treatmentGroupName: '',
        tariffId: priceResult.tariffId, // Guardar la tarifa usada
        societyId: null,
        quantity: quantity,
        priceOverride: null,
        notes: '',
        sortOrder: templateItems.length,
        priceInfo: {
          finalPrice: priceResult.price,
          source: priceResult.source,
          sourceLabel: priceResult.sourceLabel,
        },
        lineSubtotal: priceResult.price * quantity,
      };

      setTemplateItems([...templateItems, newItem]);
      setSelectedActId('');
      setSelectedQuantity(1);
    } catch (err: any) {
      toast.error('Error al calcular precio: ' + (err.message || 'Error desconocido'));
    } finally {
      setIsAddingItem(false);
    }
  };

  // Handler para confirmar añadir acto con centros afectados
  const handleConfirmAddWithAffectedSites = async () => {
    if (!pendingAddItem || !affectedSitesDialog || !editingTemplate) return;
    
    // Guardar referencias antes de limpiar estados
    const affectedSiteIds = new Set(affectedSitesDialog.affectedSites.map(s => s.siteId));
    const actToAdd = pendingAddItem.act;
    const quantityToAdd = pendingAddItem.quantity;
    const numAffected = affectedSiteIds.size;
    
    // Limpiar diálogo
    setAffectedSitesDialog(null);
    setPendingAddItem(null);
    
    // Añadir el acto
    await doAddItem(actToAdd, quantityToAdd);
    
    // Desmarcar automáticamente los centros afectados en el estado local
    const updatedSites = itemsLinkedSites.map(site => 
      affectedSiteIds.has(site.siteId) 
        ? { 
            ...site, 
            isLinked: false, 
            canLink: false,
            missingActs: [...site.missingActs, {
              actId: actToAdd.id,
              actCode: actToAdd.code,
              actName: actToAdd.name,
              defaultPrice: 0,
              reason: 'not_linked' as const,
            }] 
          } 
        : site
    );
    setItemsLinkedSites(updatedSites);
    
    // Llamar al backend para actualizar las vinculaciones inmediatamente
    try {
      await templatesApi.updateSiteLinks(editingTemplate.id, {
        links: updatedSites.map((site) => ({
          siteId: site.siteId,
          isLinked: site.isLinked,
        })),
      });
      toast.info(`${numAffected} centro${numAffected !== 1 ? 's' : ''} desvinculado${numAffected !== 1 ? 's' : ''} de la plantilla`);
    } catch (err) {
      console.error('Error al actualizar vinculaciones:', err);
      toast.warning('El acto se añadió pero hubo un error al desvincular los centros. Por favor, revisa las vinculaciones manualmente.');
    }
  };

  // Handler para cancelar añadir acto
  const handleCancelAddWithAffectedSites = () => {
    setAffectedSitesDialog(null);
    setPendingAddItem(null);
  };

  const handleRemoveItem = (index: number) => {
    setTemplateItems(templateItems.filter((_, i) => i !== index));
  };

  // ACTUALIZADO: handleUpdateItem con recálculo de precio al cambiar tarifa
  const handleUpdateItem = async (index: number, field: keyof EditableTemplateItem, value: any) => {
    // Si cambia la tarifa, recalcular el precio
    if (field === 'tariffId') {
      const item = templateItems[index];
      try {
        const priceResult = await templatesApi.calculatePrice(
          item.medicalActId,
          value || undefined // tariffId
        );

        setTemplateItems((items) => {
          const updated = [...items];
          updated[index] = {
            ...updated[index],
            tariffId: value || null,
            priceInfo: {
              finalPrice: priceResult.price,
              source: priceResult.source,
              sourceLabel: priceResult.sourceLabel,
            },
            lineSubtotal: priceResult.price * (updated[index].quantity || 1),
          };
          return updated;
        });
      } catch (err) {
        toast.error('Error al recalcular precio');
      }
      return;
    }

    // Para otros campos
    setTemplateItems((items) => {
      const updated = [...items];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalcular subtotal si cambia cantidad
      if (field === 'quantity' && updated[index].priceInfo) {
        updated[index].lineSubtotal = updated[index].priceInfo!.finalPrice * value;
      }
      
      // Si cambia priceOverride, recalcular
      if (field === 'priceOverride') {
        const finalPrice = value && value > 0 
          ? value 
          : updated[index].priceInfo?.finalPrice || 0;
        updated[index].lineSubtotal = finalPrice * (updated[index].quantity || 1);
        if (value && value > 0) {
          updated[index].priceInfo = {
            finalPrice: value,
            source: 'override',
            sourceLabel: 'Precio manual',
          };
        }
      }
      
      return updated;
    });
  };

  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === templateItems.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const items = [...templateItems];
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    items.forEach((item, i) => {
      item.sortOrder = i;
    });
    setTemplateItems(items);
  };

  const handleSaveItems = async () => {
    if (!editingTemplate) return;

    setIsSaving(true);
    try {
      await templatesApi.updateItems(editingTemplate.id, {
        items: templateItems.map((item, index) => ({
          medicalActId: item.medicalActId,
          tariffId: item.tariffId || null,
          societyId: item.societyId || null,
          quantity: item.quantity || 1,
          priceOverride: item.priceOverride || null,
          notes: item.notes || '',
          sortOrder: index,
        })),
      });
      toast.success('Líneas actualizadas correctamente');
      handleCloseItemsModal();
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Error al guardar líneas');
    } finally {
      setIsSaving(false);
    }
  };

  const estimatedTotal = useMemo(() => {
    return templateItems.reduce((sum, item) => {
      const price = item.priceOverride && item.priceOverride > 0 
        ? item.priceOverride 
        : (item.priceInfo?.finalPrice || 0);
      const qty = item.quantity || 1;
      return sum + price * qty;
    }, 0);
  }, [templateItems]);

  // --------------------------------------------------------------------------
  // HANDLERS: LINK MODAL
  // --------------------------------------------------------------------------
  const handleOpenLinkModal = async (template: Template) => {
    setLinkingTemplate(template);
    setIsLinkModalOpen(true);
    setIsLoadingLinks(true);

    try {
      const sites = await templatesApi.getLinkedSites(template.id);
      setLinkedSites(sites);
    } catch (err) {
      toast.error('Error al cargar centros');
    } finally {
      setIsLoadingLinks(false);
    }
  };

  const handleCloseLinkModal = () => {
    setIsLinkModalOpen(false);
    setLinkingTemplate(null);
    setLinkedSites([]);
  };

  const handleToggleSiteLink = (siteId: string) => {
    setLinkedSites((sites) =>
      sites.map((site) =>
        site.siteId === siteId ? { ...site, isLinked: !site.isLinked } : site
      )
    );
  };

  const handleSaveLinks = async () => {
    if (!linkingTemplate) return;

    setIsSavingLinks(true);
    try {
      await templatesApi.updateSiteLinks(linkingTemplate.id, {
        links: linkedSites.map((site) => ({
          siteId: site.siteId,
          isLinked: site.isLinked,
        })),
      });
      toast.success('Vinculaciones actualizadas');
      handleCloseLinkModal();
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Error al guardar vinculaciones');
    } finally {
      setIsSavingLinks(false);
    }
  };

  // --------------------------------------------------------------------------
  // HANDLERS: QUICK LINK
  // --------------------------------------------------------------------------
  const handleOpenQuickLink = (site: TemplateLinkedSite, act: MissingActInfo) => {
    setQuickLinkSite(site);
    setQuickLinkAct(act);
    setQuickLinkPrice(act.defaultPrice);
    setQuickLinkSocieties(site.defaultSocietyId ? [site.defaultSocietyId] : []);
    setIsQuickLinkModalOpen(true);
  };

  const handleCloseQuickLink = () => {
    setIsQuickLinkModalOpen(false);
    setQuickLinkSite(null);
    setQuickLinkAct(null);
    setQuickLinkPrice(0);
    setQuickLinkSocieties([]);
  };

  const handleSaveQuickLink = async () => {
    if (!quickLinkSite || !quickLinkAct) return;

    setIsSavingQuickLink(true);
    try {
      await templatesApi.quickLinkAct({
        medicalActId: quickLinkAct.actId,
        siteId: quickLinkSite.siteId,
        priceOverride: quickLinkPrice > 0 ? quickLinkPrice : undefined,
        societyIds: quickLinkSocieties.length > 0 ? quickLinkSocieties : undefined,
      });
      toast.success(`Acto "${quickLinkAct.actCode}" vinculado al centro`);
      
      if (linkingTemplate) {
        const sites = await templatesApi.getLinkedSites(linkingTemplate.id);
        setLinkedSites(sites);
      }
      
      handleCloseQuickLink();
    } catch (err: any) {
      toast.error(err.message || 'Error al vincular acto');
    } finally {
      setIsSavingQuickLink(false);
    }
  };

  // --------------------------------------------------------------------------
  // HANDLERS: PREVIEW CON SELECTOR DE CENTRO
  // --------------------------------------------------------------------------
  const handleOpenPreview = async (template: Template) => {
    setPreviewTemplate(template);
    setIsPreviewModalOpen(true);
    setIsLoadingPreview(true);
    setPreviewSiteId('');

    try {
      // Cargar centros vinculados a esta plantilla
      const sites = await templatesApi.getLinkedSites(template.id);
      const linkedSites = sites
        .filter(s => s.isLinked && s.canLink)
        .map(s => ({ id: s.siteId, code: s.siteCode, name: s.siteName }));
      setPreviewSites(linkedSites);

      // Cargar preview sin centro específico (precios base)
      const preview = await templatesApi.getPreview(template.id);
      setPreviewData(preview);
    } catch (err) {
      toast.error('Error', 'Error al cargar previsualización');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleChangePreviewSite = async (siteId: string) => {
    if (!previewTemplate) return;
    
    setPreviewSiteId(siteId);
    setIsLoadingPreview(true);

    try {
      const preview = await templatesApi.getPreview(previewTemplate.id, siteId || undefined);
      setPreviewData(preview);
    } catch (err) {
      toast.error('Error', 'Error al recalcular precios');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setIsPreviewModalOpen(false);
    setPreviewTemplate(null);
    setPreviewData(null);
    setPreviewSiteId('');
    setPreviewSites([]);
  };

  // --------------------------------------------------------------------------
  // HANDLERS: TOGGLE STATUS - Igual que Specialties
  // --------------------------------------------------------------------------
  const handleToggleStatus = (item: Template) => {
    setConfirmDialog({
      isOpen: true,
      item,
      action: item.isActive ? 'deactivate' : 'activate',
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await templatesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivada', `Plantilla "${confirmDialog.item.name}" desactivada`);
      } else {
        await templatesApi.reactivate(confirmDialog.item.id);
        toast.success('Activada', `Plantilla "${confirmDialog.item.name}" activada`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // --------------------------------------------------------------------------
  // HANDLERS: IMPORT/EXPORT
  // --------------------------------------------------------------------------
  const handleExport = async () => {
    try {
      const blob = await templatesApi.exportExcel();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantillas.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Archivo exportado');
    } catch (err) {
      toast.error('Error al exportar');
    }
  };

  const handleImport = async (file: File): Promise<ImportResult> => {
    try {
      const result = await templatesApi.importExcel(file);
      if (result.errors.length > 0) {
        toast.warning(`Importación parcial: ${result.created} creados, ${result.updated} actualizados`);
      } else {
        toast.success(`Importación completada: ${result.created} creados, ${result.updated} actualizados`);
      }
      loadData();
      return {
        success: result.errors.length === 0,
        created: result.created,
        updated: result.updated,
        skipped: 0,
        errors: result.errors.map((message, index) => ({ row: index + 1, message })),
      };
    } catch (err: any) {
      toast.error(err.message || 'Error al importar');
      throw err;
    }
  };
  // --------------------------------------------------------------------------
  // RENDER: TABLE
  // --------------------------------------------------------------------------
  const renderTable = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-red-500">
          <AlertCircle className="w-12 h-12 mb-2" />
          <p>{error}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reintentar
          </button>
        </div>
      );
    }

    if (!data || data.data.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <FileText className="w-12 h-12 mb-2" />
          <p>No hay plantillas</p>
          <button
            onClick={() => handleOpenModal()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Crear plantilla
          </button>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actos</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Centros</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Est.</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.data.map((template) => (
              <tr key={template.id} className={!template.isActive ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="font-mono text-sm font-medium text-gray-900">{template.code}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900">{template.name}</div>
                  {template.description && (
                    <div className="text-xs text-gray-500 truncate max-w-xs">{template.description}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    onClick={() => handleOpenItemsModal(template)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                    title="Editar actos de la plantilla"
                  >
                    <Stethoscope className="h-4 w-4" />
                    {template.itemsCount} actos
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {template.linkedSitesCount}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {template.estimatedTotal !== undefined ? formatCurrency(template.estimatedTotal) : '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    onClick={() => handleToggleStatus(template)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      template.isActive ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={template.isActive}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                        template.isActive ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    >
                      <Power
                        className={`h-3 w-3 absolute top-1 left-1 ${
                          template.isActive ? 'text-blue-600' : 'text-gray-400'
                        }`}
                      />
                    </span>
                  </button>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleOpenPreview(template)}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Previsualizar"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenLinkModal(template)}
                      className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                      title="Vincular centros"
                    >
                      <Link2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenModal(template)}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // RENDER: PAGINATION
  // --------------------------------------------------------------------------
  const renderPagination = () => {
    if (!data || data.meta.totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t">
        <div className="text-sm text-gray-500">
          Mostrando {(page - 1) * limit + 1} - {Math.min(page * limit, data.meta.total)} de {data.meta.total}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!data.meta.hasPrevPage}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm">Página {page} de {data.meta.totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(data.meta.totalPages, p + 1))}
            disabled={!data.meta.hasNextPage}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Plantillas de Presupuesto</h1>
        <p className="text-gray-500">Gestiona las plantillas predefinidas para crear presupuestos</p>
      </div>

      {/* Toolbar - Nuevo diseño */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4 mb-6">
        {/* Fila superior: Búsqueda y acciones principales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Búsqueda y botón filtros */}
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por código o nombre..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters 
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nueva plantilla
            </button>
          </div>
        </div>

        {/* Fila de filtros activos (chips) */}
        {(showInactive || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Estado: Incluye inactivos
                <button 
                  onClick={() => { setShowInactive(false); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button 
                  onClick={() => { setSearchQuery(''); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setShowInactive(false);
                setSearchQuery('');
                setPage(1);
              }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Fila inferior: Ordenación y paginación */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 pt-3 gap-4">
            {/* Checkbox y ordenación */}
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Mostrar inactivos
                </span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                    setPage(1);
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Fecha (Más reciente)</option>
                  <option value="createdAt-asc">Fecha (Antiguo)</option>
                </select>
              </div>
            </div>

            {/* Items por página */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Ver:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => { setLimit(10); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 10 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  10
                </button>
                <button
                  onClick={() => { setLimit(25); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 25 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  25
                </button>
                <button
                  onClick={() => { setLimit(50); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    limit === 50 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  50
                </button>
              </div>
              <span className="text-sm text-gray-500">por página</span>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow">
        {renderTable()}
        {renderPagination()}
      </div>

      {/* Modal Crear/Editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Editar Plantilla' : 'Nueva Plantilla'}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          <div className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {formError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
              <input
                {...register('code', {
                  required: 'El código es obligatorio',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                  maxLength: { value: 20, message: 'Máximo 20 caracteres' },
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
                placeholder="Ej: ORTODONCIA-01"
              />
              {errors.code && <p className="mt-1 text-sm text-red-500">{errors.code.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                {...register('name', {
                  required: 'El nombre es obligatorio',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                  maxLength: { value: 100, message: 'Máximo 100 caracteres' },
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Tratamiento de Ortodoncia Completo"
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                {...register('description', {
                  maxLength: { value: 500, message: 'Máximo 500 caracteres' },
                })}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Descripción opcional..."
              />
              {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>}
            </div>
          </div>

          <ModalFooter>
            <button type="button" onClick={handleCloseModal} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingItem ? 'Guardar cambios' : 'Crear plantilla'}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Editar Líneas */}
      {isItemsModalOpen && editingTemplate && (
        <TemplatesItemsModal
          template={editingTemplate}
          items={templateItems}
          availableActs={availableActs}
          availableTariffs={availableTariffs}
          availableSocieties={availableSocieties}
          isLoading={isLoadingActs}
          isSaving={isSaving}
          isAddingItem={isAddingItem}
          estimatedTotal={estimatedTotal}
          selectedActId={selectedActId}
          selectedQuantity={selectedQuantity}
          expandedItemId={expandedItemId}
          onClose={handleCloseItemsModal}
          onAddItem={handleAddItem}
          onRemoveItem={handleRemoveItem}
          onUpdateItem={handleUpdateItem}
          onMoveItem={handleMoveItem}
          onSave={handleSaveItems}
          onSelectActId={setSelectedActId}
          onSelectQuantity={setSelectedQuantity}
          onExpandItem={setExpandedItemId}
        />
      )}

      {/* Modal Vincular Centros */}
      {isLinkModalOpen && linkingTemplate && (
        <TemplatesLinkModal
          template={linkingTemplate}
          linkedSites={linkedSites}
          isLoading={isLoadingLinks}
          isSaving={isSavingLinks}
          onClose={handleCloseLinkModal}
          onToggle={handleToggleSiteLink}
          onSave={handleSaveLinks}
          onQuickLink={handleOpenQuickLink}
        />
      )}

      {/* Modal Quick Link Act */}
      {isQuickLinkModalOpen && quickLinkSite && quickLinkAct && (
        <QuickLinkActModal
          site={quickLinkSite}
          act={quickLinkAct}
          price={quickLinkPrice}
          selectedSocieties={quickLinkSocieties}
          isSaving={isSavingQuickLink}
          onClose={handleCloseQuickLink}
          onPriceChange={setQuickLinkPrice}
          onSocietiesChange={setQuickLinkSocieties}
          onSave={handleSaveQuickLink}
        />
      )}

      {/* Modal Preview con Selector de Centro */}
      {isPreviewModalOpen && previewTemplate && (
        <TemplatesPreviewModal
          template={previewTemplate}
          preview={previewData}
          isLoading={isLoadingPreview}
          sites={previewSites}
          selectedSiteId={previewSiteId}
          onSiteChange={handleChangePreviewSite}
          onClose={handleClosePreview}
        />
      )}

      {/* Confirm Dialog - Unificado igual que Specialties */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar plantilla?' : '¿Activar plantilla?'}
        message={
          confirmDialog.action === 'deactivate'
            ? `La plantilla "${confirmDialog.item?.name}" será desactivada y no estará disponible para crear presupuestos.`
            : `La plantilla "${confirmDialog.item?.name}" será reactivada.`
        }
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />

      {/* Diálogo de confirmación para centros afectados al añadir acto */}
      {affectedSitesDialog && (
        <Modal
          isOpen={affectedSitesDialog.isOpen}
          onClose={handleCancelAddWithAffectedSites}
          title="Centros serán desvinculados"
          size="md"
        >
          <div className="space-y-4">
            {/* Aviso principal */}
            <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    El acto <strong>{affectedSitesDialog.actCode} - {affectedSitesDialog.actName}</strong> no está vinculado a los siguientes centros:
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de centros afectados */}
            <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
              {affectedSitesDialog.affectedSites.map((site) => (
                <div key={site.siteId} className="p-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{site.siteName}</span>
                  <span className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                    {site.siteCode}
                  </span>
                </div>
              ))}
            </div>

            {/* Explicación */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700">
                Si añades este acto a la plantilla, {affectedSitesDialog.affectedSites.length === 1 
                  ? 'este centro será desvinculado' 
                  : 'estos centros serán desvinculados'} automáticamente hasta que vincules el acto a {affectedSitesDialog.affectedSites.length === 1 ? 'él' : 'ellos'}.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Para vincular el acto a un centro, ve a <strong>Actos médicos → Editar → Vincular centros</strong>.
              </p>
            </div>
          </div>

          <ModalFooter>
            <button
              onClick={handleCancelAddWithAffectedSites}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmAddWithAffectedSites}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Añadir y desvincular centros
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* Import/Export */}
      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        title="Plantillas"
        onExport={handleExport}
        onImport={handleImport}
      />
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

// ----------------------------------------------------------------------------
// MODAL: EDITAR LÍNEAS
// ----------------------------------------------------------------------------
interface TemplatesItemsModalProps {
  template: Template;
  items: EditableTemplateItem[];
  availableActs: MedicalActSelect[];
  availableTariffs: TariffInfo[];
  availableSocieties: SocietyInfo[];
  isLoading: boolean;
  isSaving: boolean;
  isAddingItem: boolean;
  estimatedTotal: number;
  selectedActId: string;
  selectedQuantity: number;
  expandedItemId: string | null;
  onClose: () => void;
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  onUpdateItem: (index: number, field: keyof EditableTemplateItem, value: any) => void;
  onMoveItem: (index: number, direction: 'up' | 'down') => void;
  onSave: () => void;
  onSelectActId: (id: string) => void;
  onSelectQuantity: (qty: number) => void;
  onExpandItem: (id: string | null) => void;
}

function TemplatesItemsModal({
  template,
  items,
  availableActs,
  availableTariffs,
  availableSocieties,
  isLoading,
  isSaving,
  isAddingItem,
  estimatedTotal,
  selectedActId,
  selectedQuantity,
  expandedItemId,
  onClose,
  onAddItem,
  onRemoveItem,
  onUpdateItem,
  onMoveItem,
  onSave,
  onSelectActId,
  onSelectQuantity,
  onExpandItem,
}: TemplatesItemsModalProps) {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const availableActsFiltered = availableActs.filter(
    (act) => !items.some((item) => item.medicalActId === act.id)
  );

  const handleCancelClick = () => {
    setShowCloseConfirm(true);
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  return (
    <>
      <Modal 
        isOpen={true} 
        onClose={handleCancelClick} 
        title={`Editar líneas: ${template.name}`} 
        size="xl"
        closeOnOverlayClick={false}
        closeOnEscape={false}
      >
        {/* Contenedor principal con altura máxima y layout flex */}
        <div className="flex flex-col" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          {/* Añadir nuevo acto - Siempre visible arriba */}
          <div className="flex-shrink-0 p-4 bg-gray-50 rounded-lg border mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Añadir acto</h4>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Acto médico</label>
                <select
                  value={selectedActId}
                  onChange={(e) => onSelectActId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  disabled={isLoading || isAddingItem}
                >
                  <option value="">Seleccionar acto...</option>
                  {availableActsFiltered.map((act) => (
                    <option key={act.id} value={act.id}>
                      {act.code} - {act.name}
                      {act.defaultPrice > 0 && ` (${formatCurrency(act.defaultPrice)})`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Cantidad</label>
                <input
                  type="number"
                  min={1}
                  value={selectedQuantity}
                  onChange={(e) => onSelectQuantity(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg text-sm text-center"
                  disabled={isAddingItem}
                />
              </div>
              <button
                onClick={onAddItem}
                disabled={!selectedActId || isLoading || isAddingItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isAddingItem ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Añadir
              </button>
            </div>
          </div>

          {/* Lista de líneas - Con scroll propio */}
          <div className="flex-1 min-h-0 border rounded-lg overflow-hidden flex flex-col">
            {items.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay actos en esta plantilla</p>
                <p className="text-sm">Añade actos usando el selector de arriba</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto divide-y">
                {items.map((item, index) => (
                  <div
                    key={item.medicalActId}
                    className={`p-4 ${expandedItemId === item.medicalActId ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                  >
                    {/* Fila principal */}
                    <div className="flex items-center gap-3">
                      {/* Orden */}
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => onMoveItem(index, 'up')}
                          disabled={index === 0}
                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <GripVertical className="w-4 h-4 text-gray-300" />
                        <button
                          onClick={() => onMoveItem(index, 'down')}
                          disabled={index === items.length - 1}
                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-gray-900">{item.medicalActCode}</span>
                          <span className="text-sm text-gray-600 truncate">{item.medicalActName}</span>
                        </div>
                        <div className="text-xs text-gray-400">{item.treatmentGroupName}</div>
                      </div>

                      {/* Cantidad */}
                      <div className="w-20">
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => onUpdateItem(index, 'quantity', Number(e.target.value))}
                          className="w-full px-2 py-1 border rounded text-sm text-center"
                        />
                      </div>

                      {/* Precio y origen */}
                      <div className="w-32 text-right">
                        <div className="text-sm font-medium">{formatCurrency(item.priceInfo?.finalPrice || 0)}</div>
                        {item.priceInfo && (
                          <PriceSourceBadge source={item.priceInfo.source} sourceLabel={item.priceInfo.sourceLabel} />
                        )}
                      </div>

                      {/* Subtotal */}
                      <div className="w-28 text-right">
                        <div className="text-sm font-bold text-gray-900">{formatCurrency(item.lineSubtotal || 0)}</div>
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onExpandItem(expandedItemId === item.medicalActId ? null : item.medicalActId)}
                          className={`p-1.5 rounded ${
                            expandedItemId === item.medicalActId
                              ? 'bg-blue-100 text-blue-600'
                              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Configurar tarifa y sociedad"
                        >
                          <Tag className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onRemoveItem(index)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Panel expandido */}
                    {expandedItemId === item.medicalActId && (
                      <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Tag className="w-3 h-3 inline mr-1" />
                            Tarifa específica
                          </label>
                          <select
                            value={item.tariffId || ''}
                            onChange={(e) => onUpdateItem(index, 'tariffId', e.target.value || null)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          >
                            <option value="">Usar tarifa por defecto (Privado)</option>
                            {availableTariffs.map((tariff) => (
                              <option key={tariff.id} value={tariff.id}>
                                {tariff.insuranceCompanyName} - {tariff.policyName} - {tariff.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Building2 className="w-3 h-3 inline mr-1" />
                            Sociedad facturadora
                          </label>
                          <select
                            value={item.societyId || ''}
                            onChange={(e) => onUpdateItem(index, 'societyId', e.target.value || null)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          >
                            <option value="">Sin asignar</option>
                            {availableSocieties.map((society) => (
                              <option key={society.id} value={society.id}>
                                {society.code} - {society.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            <DollarSign className="w-3 h-3 inline mr-1" />
                            Precio manual (override)
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={item.priceOverride ?? ''}
                              onChange={(e) =>
                                onUpdateItem(index, 'priceOverride', e.target.value ? Number(e.target.value) : null)
                              }
                              placeholder="Dejar vacío para usar precio calculado"
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Info className="w-3 h-3 inline mr-1" />
                            Notas de la línea
                          </label>
                          <input
                            type="text"
                            value={item.notes || ''}
                            onChange={(e) => onUpdateItem(index, 'notes', e.target.value)}
                            placeholder="Notas opcionales..."
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            maxLength={255}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Total - Siempre visible abajo */}
          {items.length > 0 && (
            <div className="flex-shrink-0 mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-700">
                  <Calculator className="w-5 h-5" />
                  <span className="font-medium">Total estimado</span>
                  <span className="text-xs text-blue-500">({items.length} {items.length === 1 ? 'acto' : 'actos'})</span>
                </div>
                <span className="text-2xl font-bold text-blue-900">{formatCurrency(estimatedTotal)}</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Este total es una estimación. El precio final puede variar según el centro y tarifa.
              </p>
            </div>
          )}
        </div>

        <ModalFooter>
          <button onClick={handleCancelClick} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" />
            Guardar cambios
          </button>
        </ModalFooter>
      </Modal>

      {/* Diálogo de confirmación para cerrar */}
      <ConfirmDialog
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={handleConfirmClose}
        title="¿Cerrar sin guardar?"
        message="Los cambios realizados en las líneas de la plantilla no se guardarán. ¿Estás seguro de que quieres cerrar?"
        confirmText="Cerrar sin guardar"
        cancelText="Continuar editando"
        variant="warning"
      />
    </>
  );
}

// ----------------------------------------------------------------------------
// MODAL: VINCULAR CENTROS - CON AVISOS CLAROS DE ACTOS FALTANTES
// ----------------------------------------------------------------------------
interface TemplatesLinkModalProps {
  template: Template;
  linkedSites: TemplateLinkedSite[];
  isLoading: boolean;
  isSaving: boolean;
  onClose: () => void;
  onToggle: (siteId: string) => void;
  onSave: () => void;
  onQuickLink: (site: TemplateLinkedSite, act: MissingActInfo) => void;
}

function TemplatesLinkModal({
  template,
  linkedSites,
  isLoading,
  isSaving,
  onClose,
  onToggle,
  onSave,
  onQuickLink,
}: TemplatesLinkModalProps) {
  // Contar centros con problemas
  const sitesWithIssues = linkedSites.filter(s => s.missingActs.length > 0).length;
  const linkedCount = linkedSites.filter(s => s.isLinked).length;

  return (
    <Modal isOpen={true} onClose={onClose} title={`Vincular centros: ${template.name}`} size="lg">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Resumen y explicación */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <Info className="w-4 h-4 inline mr-2" />
              Selecciona los centros donde estará disponible esta plantilla. 
              Solo podrás vincular centros que tengan <strong>todos los actos</strong> de la plantilla configurados.
            </p>
          </div>

          {/* Warning si hay centros con problemas */}
          {sitesWithIssues > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {sitesWithIssues} centro{sitesWithIssues !== 1 ? 's' : ''} con actos no vinculados
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Para vincular estos centros, primero debes ir a <strong>Actos médicos → Editar → Vincular centros</strong> y 
                    añadir los actos faltantes, o usar el botón "Vincular" junto a cada acto para hacerlo rápidamente.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Lista de centros */}
          <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
            {linkedSites.map((site) => {
              const hasIssues = site.missingActs.length > 0;
              const isBlocked = hasIssues && !site.isLinked;
              
              return (
                <div 
                  key={site.siteId} 
                  className={`p-4 ${
                    site.isLinked 
                      ? 'bg-green-50' 
                      : hasIssues 
                        ? 'bg-amber-50/50' 
                        : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <input
                        type="checkbox"
                        checked={site.isLinked}
                        onChange={() => onToggle(site.siteId)}
                        disabled={isBlocked}
                        className={`rounded border-gray-300 ${
                          isBlocked 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'text-blue-600 cursor-pointer'
                        }`}
                      />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{site.siteName}</span>
                        <span className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded">{site.siteCode}</span>
                        
                        {site.canLink ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Compatible
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {site.missingActs.length} acto{site.missingActs.length !== 1 ? 's' : ''} sin vincular
                          </span>
                        )}
                      </div>

                      {/* Lista de actos faltantes */}
                      {hasIssues && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-800 font-medium mb-2 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" />
                            Los siguientes actos de la plantilla NO están vinculados a este centro:
                          </p>
                          <div className="space-y-2">
                            {site.missingActs.map((act) => (
                              <div 
                                key={act.actId} 
                                className="flex items-center justify-between bg-white p-2 rounded border border-amber-200"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm font-medium text-amber-900">{act.actCode}</span>
                                  <span className="text-sm text-amber-700">{act.actName}</span>
                                  {act.defaultPrice > 0 && (
                                    <span className="text-xs text-gray-500">
                                      ({formatCurrency(act.defaultPrice)})
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => onQuickLink(site, act)}
                                  className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
                                  title="Vincular este acto al centro"
                                >
                                  <LinkIcon className="w-3 h-3" />
                                  Vincular ahora
                                </button>
                              </div>
                            ))}
                          </div>
                          <p className="text-[10px] text-amber-600 mt-2 italic">
                            Hasta que vincules estos actos, no podrás activar este centro para la plantilla.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Resumen de vinculaciones */}
          <div className="text-sm text-gray-600 text-center">
            {linkedCount} de {linkedSites.length} centros vinculados
          </div>
        </div>
      )}

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          <Save className="w-4 h-4" />
          Guardar vinculaciones
        </button>
      </ModalFooter>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// MODAL: QUICK LINK ACT
// ----------------------------------------------------------------------------
interface QuickLinkActModalProps {
  site: TemplateLinkedSite;
  act: MissingActInfo;
  price: number;
  selectedSocieties: string[];
  isSaving: boolean;
  onClose: () => void;
  onPriceChange: (price: number) => void;
  onSocietiesChange: (societies: string[]) => void;
  onSave: () => void;
}

function QuickLinkActModal({
  site,
  act,
  price,
  selectedSocieties,
  isSaving,
  onClose,
  onPriceChange,
  onSocietiesChange,
  onSave,
}: QuickLinkActModalProps) {
  const toggleSociety = (societyId: string) => {
    if (selectedSocieties.includes(societyId)) {
      onSocietiesChange(selectedSocieties.filter((id) => id !== societyId));
    } else {
      onSocietiesChange([...selectedSocieties, societyId]);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Vincular acto al centro" size="md">
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-800">
            <strong>Acto:</strong> {act.actCode} - {act.actName}
          </div>
          <div className="text-sm text-blue-800 mt-1">
            <strong>Centro:</strong> {site.siteName} ({site.siteCode})
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <DollarSign className="w-4 h-4 inline mr-1" />
            Precio en el centro
          </label>
          <div className="relative">
            <input
              type="number"
              min={0}
              step={0.01}
              value={price}
              onChange={(e) => onPriceChange(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="0 = usar precio del acto"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Precio por defecto del acto: {formatCurrency(act.defaultPrice)}
          </p>
        </div>

        {site.availableSocieties.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Sociedades facturadoras
            </label>
            <div className="space-y-2 border rounded-lg p-3 max-h-48 overflow-y-auto">
              {site.availableSocieties.map((society) => (
                <label key={society.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSocieties.includes(society.id)}
                    onChange={() => toggleSociety(society.id)}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm">
                    {society.code} - {society.name}
                    {site.defaultSocietyId === society.id && (
                      <span className="text-xs text-blue-600 ml-1">(por defecto)</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          <LinkIcon className="w-4 h-4" />
          Vincular acto
        </button>
      </ModalFooter>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// MODAL: PREVIEW CON SELECTOR DE CENTRO
// ----------------------------------------------------------------------------
interface TemplatesPreviewModalProps {
  template: Template;
  preview: TemplatePreview | null;
  isLoading: boolean;
  sites: Array<{ id: string; code: string; name: string }>;
  selectedSiteId: string;
  onSiteChange: (siteId: string) => void;
  onClose: () => void;
}

function TemplatesPreviewModal({ 
  template, 
  preview, 
  isLoading, 
  sites,
  selectedSiteId,
  onSiteChange,
  onClose 
}: TemplatesPreviewModalProps) {
  return (
    <Modal isOpen={true} onClose={onClose} title={`Previsualización: ${template.name}`} size="lg">
      {/* Selector de centro */}
      <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-gray-700">Ver precios para:</span>
          </div>
          <select
            value={selectedSiteId}
            onChange={(e) => onSiteChange(e.target.value)}
            disabled={isLoading}
            className="flex-1 px-3 py-2 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="">-- Precios base (sin centro) --</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name} ({site.code})
              </option>
            ))}
          </select>
        </div>
        {selectedSiteId && preview?.siteName && (
          <div className="mt-2 flex items-center gap-2 text-sm text-blue-700">
            <Info className="w-4 h-4" />
            <span>Los precios mostrados incluyen los precios específicos del centro <strong>{preview.siteName}</strong></span>
          </div>
        )}
        {sites.length === 0 && (
          <div className="mt-2 flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4" />
            <span>Esta plantilla no está vinculada a ningún centro</span>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : preview ? (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Plantilla:</span>
                <span className="ml-2 font-medium">{preview.templateCode}</span>
              </div>
              <div>
                <span className="text-gray-500">Actos:</span>
                <span className="ml-2 font-medium">{preview.totalItems}</span>
              </div>
              <div>
                <span className="text-gray-500">Cantidad total:</span>
                <span className="ml-2 font-medium">{preview.totalQuantity}</span>
              </div>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Acto</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cant.</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Precio</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Origen</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {preview.lines.map((line, index) => (
                  <tr key={index} className={line.priceSource.includes('centro') ? 'bg-green-50' : ''}>
                    <td className="px-4 py-2">
                      <div className="text-sm font-medium text-gray-900">{line.medicalActCode}</div>
                      <div className="text-xs text-gray-500">{line.medicalActName}</div>
                      {line.tariffName && (
                        <div className="text-xs text-purple-600">
                          <Tag className="w-3 h-3 inline mr-1" />
                          {line.tariffName}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-sm">{line.quantity}</td>
                    <td className="px-4 py-2 text-right text-sm">{formatCurrency(line.unitPrice)}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        line.priceSource.includes('centro') 
                          ? 'bg-green-100 text-green-800' 
                          : line.priceSource.includes('Tarifa')
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {line.priceSource}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-medium">{formatCurrency(line.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right font-medium text-gray-700">
                    Total estimado{selectedSiteId && preview.siteName ? ` (${preview.siteName})` : ''}:
                  </td>
                  <td className="px-4 py-3 text-right text-xl font-bold text-blue-900">
                    {formatCurrency(preview.subtotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-xs text-gray-500 text-center">
            {selectedSiteId 
              ? 'Los precios mostrados incluyen los precios específicos del centro seleccionado.'
              : 'Selecciona un centro para ver los precios específicos de ese centro.'
            }
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <AlertCircle className="w-8 h-8 mr-2" />
          No se pudo cargar la previsualización
        </div>
      )}

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
          Cerrar
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default TemplatesAdminPage;
